import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

const BlogCreateForm = forwardRef(
  ({ onSave, submitting, initialData, onCancel }, ref) => {
    const [title, setTitle] = useState("");
    const [blogDescription, setBlogDescription] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const editorContainerRef = useRef(null);
    const quillInstanceRef = useRef(null);

    useEffect(() => {
      if (editorContainerRef.current && !quillInstanceRef.current) {
        quillInstanceRef.current = new Quill(editorContainerRef.current, {
          theme: "snow",
          placeholder: "Write full blog content...",
          modules: {
            toolbar: [
              ["bold", "italic", "underline", "strike"],
              [{ list: "ordered" }, { list: "bullet" }],
              [{ align: [] }],
              ["link"],
              ["clean"],
            ],
          },
        });
      }
    }, []);

    useEffect(() => {
      const blog = initialData || null;
      if (!blog) {
        setTitle("");
        setBlogDescription("");
        setImageUrl("");
        if (quillInstanceRef.current) {
          quillInstanceRef.current.root.innerHTML = "";
        }
        return;
      }

      setTitle(blog.title || blog.name || "");
      setBlogDescription(blog.blog_description || "");
      setImageUrl(blog.image_url || "");

      if (quillInstanceRef.current) {
        quillInstanceRef.current.root.innerHTML = blog.description || "";
      }
    }, [initialData]);

    const handleSubmit = async (e) => {
      if (e) {
        e.preventDefault();
      }
      if (!title.trim() || submitting) return;

      const descriptionHtml = quillInstanceRef.current
        ? quillInstanceRef.current.root.innerHTML
        : "";

      const payload = {
        title: title.trim(),
        blog_description: blogDescription.trim(),
        description: descriptionHtml,
        image_url: imageUrl.trim(),
      };

      await onSave(payload);
    };

    const isDisabled = submitting || !title.trim();

    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
    }));

    return (
      <div className="blog-create-container">
        <form className="blog-create-form" onSubmit={handleSubmit}>
          <div className="blog-create-actions">
            <button
              type="submit"
              className="blog-create-btn blog-create-btn-primary"
              disabled={isDisabled}
            >
              Save
            </button>
            <button
              type="button"
              className="blog-create-btn blog-create-btn-primary"
              onClick={() => onCancel && onCancel()}
              disabled={submitting}
            >
              ×
            </button>
          </div>

          <div className="blog-create-row">
            <label className="blog-create-label" htmlFor="blog-create-title">
              Title
            </label>
            <input
              id="blog-create-title"
              type="text"
              className="blog-create-input"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="blog-create-row">
            <label className="blog-create-label" htmlFor="blog-create-image">
              Upload Image
            </label>
            <input
              id="blog-create-image"
              type="text"
              className="blog-create-input"
              placeholder="Image URL"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </div>

          <div className="blog-create-row">
            <label
              className="blog-create-label"
              htmlFor="blog-create-short-description"
            >
              Description
            </label>
            <textarea
              id="blog-create-short-description"
              className="blog-create-input blog-create-textarea"
              rows={3}
              placeholder="Short description"
              value={blogDescription}
              onChange={(e) => setBlogDescription(e.target.value)}
            />
          </div>

          <div className="blog-create-row">
            <label className="blog-create-label">Content</label>
            <div className="blog-create-editor" ref={editorContainerRef} />
          </div>
        </form>
      </div>
    );
  },
);

export default BlogCreateForm;
